import React, { useState } from 'react';
import { TestDetailsModalProps, TestType } from '../../types';
import { testCasesApi, handleApiError } from '../../config/api';

const TestDetailsModal: React.FC<TestDetailsModalProps> = ({
    isOpen,
    onClose,
    testDetails,
    isEditMode,
    setIsEditMode,
    editedTestDetails,
    setEditedTestDetails,
    isSaving,
    onSaveChanges
}) => {
    const [localIsSaving, setLocalIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

    // Validate form fields
    const validateFields = (): boolean => {
        const errors: {[key: string]: string} = {};

        if (!editedTestDetails?.task_prompt?.trim()) {
            errors.task_prompt = 'Task prompt is required';
        }

        if (!editedTestDetails?.expected_outcome?.trim()) {
            errors.expected_outcome = 'Expected outcome is required';
        }

        if (!editedTestDetails?.type) {
            errors.type = 'Test type must be selected';
        }

        // Validate JSON fields
        try {
            if (typeof editedTestDetails?.mock_data === 'string') {
                JSON.parse(editedTestDetails.mock_data);
            }
        } catch (e) {
            errors.mock_data = 'Mock data must be valid JSON';
        }

        try {
            if (typeof editedTestDetails?.simulation_config === 'string') {
                JSON.parse(editedTestDetails.simulation_config);
            }
        } catch (e) {
            errors.simulation_config = 'Simulation config must be valid JSON';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Real save implementation with proper error handling
    const handleRealSaveChanges = async () => {
        if (!editedTestDetails || !testDetails) {
            setSaveStatus(' Missing test details for save operation');
            return;
        }

        // Validate fields first
        if (!validateFields()) {
            setSaveStatus(' Please fix validation errors before saving');
            return;
        }

        setLocalIsSaving(true);
        setSaveStatus(' Saving test case changes to database...');

        try {
            // Prepare data for API call
            const updateData = {
                task_prompt: editedTestDetails.task_prompt.trim(),
                expected_outcome: editedTestDetails.expected_outcome.trim(),
                mock_data: typeof editedTestDetails.mock_data === 'string'
                    ? JSON.parse(editedTestDetails.mock_data)
                    : editedTestDetails.mock_data,
                simulation_config: typeof editedTestDetails.simulation_config === 'string'
                    ? JSON.parse(editedTestDetails.simulation_config)
                    : editedTestDetails.simulation_config,
                type: editedTestDetails.type
            };

            // Call real API to save changes
            const result = await testCasesApi.update(
                testDetails.agent_name || 'unknown',
                testDetails.name,
                updateData
            );

            if (result.status === 'success') {
                setSaveStatus(' Test case saved successfully to database!');

                // Update parent component state
                if (onSaveChanges) {
                    await onSaveChanges();
                }

                // Exit edit mode
                setIsEditMode(false);

                // Auto-hide success message and close modal after delay
                setTimeout(() => {
                    setSaveStatus(null);
                    onClose();
                }, 2000);
            } else {
                throw new Error(result.message || 'Save operation failed');
            }

        } catch (error: any) {
            const errorMessage = handleApiError(error);
            setSaveStatus(` Save failed: ${errorMessage}`);
            console.error('Save test case error:', error);

            // Keep modal open on error so user can retry
        } finally {
            setLocalIsSaving(false);
        }
    };

    // Handle cancel with unsaved changes warning
    const handleCancelEdit = () => {
        const hasChanges = JSON.stringify(editedTestDetails) !== JSON.stringify(testDetails);

        if (hasChanges) {
            const confirmed = window.confirm(
                ' You have unsaved changes that will be lost.\n\n' +
                'Are you sure you want to cancel editing?'
            );
            if (!confirmed) return;
        }

        setIsEditMode(false);
        setEditedTestDetails(null);
        setSaveStatus(null);
        setValidationErrors({});
    };

    // Handle JSON field changes with validation
    const handleJsonFieldChange = (field: 'mock_data' | 'simulation_config', value: string) => {
        try {
            // Test if it's valid JSON
            JSON.parse(value);
            // Clear any previous validation error
            setValidationErrors(prev => ({...prev, [field]: ''}));
        } catch (e) {
            // Set validation error but still update the field to allow typing
            setValidationErrors(prev => ({...prev, [field]: 'Invalid JSON format'}));
        }

        setEditedTestDetails({
            ...editedTestDetails!,
            [field]: value
        });
    };

    if (!isOpen || !testDetails) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content test-details-modal" onClick={e => e.stopPropagation()}
                 style={{width: '95vw', maxWidth: '1200px'}}>

                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                }}>
                    <h2> Test Case Details: {testDetails.name}</h2>
                    <div style={{display: 'flex', gap: '0.5rem'}}>
                        <button
                            onClick={() => {
                                if (isEditMode) {
                                    handleCancelEdit();
                                } else {
                                    setIsEditMode(true);
                                    setEditedTestDetails({...testDetails});
                                    setSaveStatus(null);
                                    setValidationErrors({});
                                }
                            }}
                            className="widget-button button-secondary"
                            disabled={localIsSaving || isSaving}
                        >
                            {isEditMode ? '👁️ View Mode' : '✏️ Edit Mode'}
                        </button>
                        <button onClick={onClose} disabled={localIsSaving || isSaving}>
                            ✕ Close
                        </button>
                    </div>
                </div>

                {/* Save Status Display */}
                {saveStatus && (
                    <div style={{
                        padding: '0.75rem',
                        marginBottom: '1rem',
                        borderRadius: '6px',
                        backgroundColor: saveStatus.includes('') ? '#f8d7da' :
                                       saveStatus.includes('') ? '#fff3cd' : '#d4edda',
                        color: saveStatus.includes('') ? '#721c24' :
                               saveStatus.includes('') ? '#856404' : '#155724',
                        border: `1px solid ${saveStatus.includes('') ? '#f1c2c7' : 
                                             saveStatus.includes('') ? '#ffeaa7' : '#c3e6cb'}`,
                        fontWeight: '500'
                    }}>
                        {saveStatus}
                    </div>
                )}

                {/* Loading Overlay */}
                {(localIsSaving || isSaving) && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            padding: '2rem',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            textAlign: 'center'
                        }}>
                            <div style={{fontSize: '2rem', marginBottom: '1rem'}}>💾</div>
                            <div style={{fontSize: '1.1rem', fontWeight: '500'}}>Saving to database...</div>
                        </div>
                    </div>
                )}

                {/* Form Fields */}
                <div className="details-grid">
                    {/* Test Type */}
                    <div className="detail-section">
                        <strong>Test Type:</strong>
                        {isEditMode ? (
                            <div>
                                <select
                                    value={editedTestDetails?.type || ''}
                                    onChange={(e) => setEditedTestDetails({
                                        ...editedTestDetails!,
                                        type: e.target.value as TestType
                                    })}
                                    style={{
                                        marginLeft: '0.5rem',
                                        padding: '0.25rem',
                                        border: validationErrors.type ? '2px solid #dc3545' : '1px solid #ccc'
                                    }}
                                >
                                    <option value="">Select Type</option>
                                    <option value="Functional">Functional</option>
                                    <option value="Reliability">Reliability</option>
                                    <option value="Guardrail">Guardrail</option>
                                    <option value="Compliance">Compliance</option>
                                </select>
                                {validationErrors.type && (
                                    <div style={{color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem'}}>
                                        {validationErrors.type}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span className={`test-type-badge ${testDetails.type?.toLowerCase()}`}>
                                {testDetails.type}
                            </span>
                        )}
                    </div>

                    {/* Task Prompt */}
                    <div className="detail-section">
                        <strong>Task Prompt:</strong>
                        {isEditMode ? (
                            <div>
                                <textarea
                                    style={{
                                        width: '100%',
                                        height: '100px',
                                        marginTop: '0.5rem',
                                        padding: '0.5rem',
                                        border: validationErrors.task_prompt ? '2px solid #dc3545' : '1px solid #ccc',
                                        borderRadius: '4px'
                                    }}
                                    value={editedTestDetails?.task_prompt || ''}
                                    onChange={(e) => {
                                        setEditedTestDetails({
                                            ...editedTestDetails!,
                                            task_prompt: e.target.value
                                        });
                                        if (validationErrors.task_prompt) {
                                            setValidationErrors(prev => ({...prev, task_prompt: ''}));
                                        }
                                    }}
                                    placeholder="Enter the customer request or scenario..."
                                />
                                {validationErrors.task_prompt && (
                                    <div style={{color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem'}}>
                                        {validationErrors.task_prompt}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <pre className="detail-content">{testDetails.task_prompt}</pre>
                        )}
                    </div>

                    {/* Expected Outcome */}
                    <div className="detail-section">
                        <strong>Expected Outcome:</strong>
                        {isEditMode ? (
                            <div>
                                <textarea
                                    style={{
                                        width: '100%',
                                        height: '100px',
                                        marginTop: '0.5rem',
                                        padding: '0.5rem',
                                        border: validationErrors.expected_outcome ? '2px solid #dc3545' : '1px solid #ccc',
                                        borderRadius: '4px'
                                    }}
                                    value={editedTestDetails?.expected_outcome || ''}
                                    onChange={(e) => {
                                        setEditedTestDetails({
                                            ...editedTestDetails!,
                                            expected_outcome: e.target.value
                                        });
                                        if (validationErrors.expected_outcome) {
                                            setValidationErrors(prev => ({...prev, expected_outcome: ''}));
                                        }
                                    }}
                                    placeholder="Describe what the agent should do..."
                                />
                                {validationErrors.expected_outcome && (
                                    <div style={{color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem'}}>
                                        {validationErrors.expected_outcome}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <pre className="detail-content">{testDetails.expected_outcome}</pre>
                        )}
                    </div>

                    {/* Simulation Configuration */}
                    <div className="detail-section">
                        <strong>Simulation Configuration:</strong>
                        {isEditMode ? (
                            <div>
                                <textarea
                                    style={{
                                        width: '100%',
                                        height: '120px',
                                        fontFamily: 'monospace',
                                        border: validationErrors.simulation_config ? '2px solid #dc3545' : '1px solid #ccc',
                                        borderRadius: '4px',
                                        padding: '0.5rem'
                                    }}
                                    value={typeof editedTestDetails?.simulation_config === 'object'
                                        ? JSON.stringify(editedTestDetails.simulation_config, null, 2)
                                        : editedTestDetails?.simulation_config || ''
                                    }
                                    onChange={(e) => handleJsonFieldChange('simulation_config', e.target.value)}
                                    placeholder='{"persona": "Helpful API", "scenario": "Normal Operation"}'
                                />
                                {validationErrors.simulation_config && (
                                    <div style={{color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem'}}>
                                        {validationErrors.simulation_config}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <pre className="detail-content">{JSON.stringify(testDetails.simulation_config, null, 2)}</pre>
                        )}
                    </div>

                    {/* Mock Data */}
                    <div className="detail-section">
                        <strong>Mock Data:</strong>
                        {isEditMode ? (
                            <div>
                                <textarea
                                    style={{
                                        width: '100%',
                                        height: '150px',
                                        fontFamily: 'monospace',
                                        border: validationErrors.mock_data ? '2px solid #dc3545' : '1px solid #ccc',
                                        borderRadius: '4px',
                                        padding: '0.5rem'
                                    }}
                                    value={typeof editedTestDetails?.mock_data === 'object'
                                        ? JSON.stringify(editedTestDetails.mock_data, null, 2)
                                        : editedTestDetails?.mock_data || ''
                                    }
                                    onChange={(e) => handleJsonFieldChange('mock_data', e.target.value)}
                                    placeholder='{"expected_mock_behavior": {"status_code": 200}, "payload": {}}'
                                />
                                {validationErrors.mock_data && (
                                    <div style={{color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem'}}>
                                        {validationErrors.mock_data}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <pre className="detail-content">{JSON.stringify(testDetails.mock_data, null, 2)}</pre>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="modal-actions">
                    {isEditMode ? (
                        <>
                            <button
                                onClick={handleCancelEdit}
                                disabled={localIsSaving || isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                className="widget-button button-primary"
                                onClick={handleRealSaveChanges}
                                disabled={localIsSaving || isSaving || Object.keys(validationErrors).some(key => validationErrors[key])}
                            >
                                {(localIsSaving || isSaving) ? '💾 Saving...' : '💾 Save Changes to Database'}
                            </button>
                        </>
                    ) : (
                        <button onClick={onClose}>Close</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TestDetailsModal;